import Typography from "@mui/material/Typography";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Button from "@mui/material/Button";
import * as React from "react";
import {ConfirmationSettings, LocalizedConfirmationTemplate, LocalizedMailTemplate} from "crqlar-react-common/dist/models/sector/sector.model";
import Property from "crqlar-react-common/dist/components/Property";
import {useTranslation} from "react-i18next";
import {useQuery, useQueryClient} from "react-query";
import {
    MailingConfigControllerService
} from "crqlar-react-common/dist/services/correspondence/mailingConfigController.service";
import QuickAddTransactionTypeDialog from "../sectors/QuickAddTransactionTypeDialog";
import {useNewsletterAccountQuery} from "crqlar-react-common/dist/utils/hooks/newsletter/useNewsletterAccount";
import LocalizedTransactionTypeField from "./LocalizedTransactionTypeField";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {
    TransactionalMailTemplateService
} from "crqlar-react-common/dist/services/newsletter/transactionalMailTemplate.service";
import {TransactionalMailTemplate} from "crqlar-react-common/dist/models/newsletter/transactionalMailTemplate.model";
import LocalizedMailTemplateField from "./LocalizedMailTemplateField";
import CreateTransactionalMailTemplateDialog from "./CreateTransactionalMailTemplateDialog";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";

interface ConfirmationSettingsSectionProps {
    confirmationSettings?: ConfirmationSettings;
    onSettingChange: (event: keyof ConfirmationSettings, value: any) => void;
    sx?: React.CSSProperties;
    gap?: number;
    showSectionTitle?: boolean;
    section?: 'all' | 'sendgrid' | 'other';
}

const ConfirmationSettingsSection: React.FC<ConfirmationSettingsSectionProps> = ({
                                                                                     confirmationSettings,
                                                                                     onSettingChange,
                                                                                     sx,
                                                                                     gap,
                                                                                     showSectionTitle,
                                                                                     section = 'all'
                                                                                 }) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {hasGuestAppModule} = useHasGuestAppModule();
    const newsletterAccountQ = useNewsletterAccountQuery();
    const newsletterAccount = newsletterAccountQ.data;

    const readOnlyUser = useReadOnly()
    const showSendGrid = section !== 'other';
    const showOther = section !== 'sendgrid';

    const [addNewTransactionTypeDialogOpen, setAddNewTransactionTypeDialogOpen] = React.useState(false);
    const [copiedPlaceholder, setCopiedPlaceholder] = React.useState<string | null>(null);
    const [cloningField, setCloningField] = React.useState<string | null>(null);
    const [cloneDialogTemplate, setCloneDialogTemplate] = React.useState<TransactionalMailTemplate | undefined>(undefined);
    const [editDialogTemplate, setEditDialogTemplate] = React.useState<TransactionalMailTemplate | undefined>(undefined);
    const [mailTemplateMenuAnchorEl, setMailTemplateMenuAnchorEl] = React.useState<HTMLElement | null>(null);
    const [mailTemplateMenuField, setMailTemplateMenuField] = React.useState<keyof ConfirmationSettings | null>(null);

    const placeholders = [
        '{{{serviceName}}}',
        '{{{serviceDuration}}}',
        '{{{dateAndTime}}}',
        '{{{guestNote}}}',
        '{{{date}}}',
        '{{{time}}}',
        '{{{name}}}',
        '{{{confirmationNumber}}}',
        '{{{confirmationCode}}}',
        '{{{streetAddress}}}',
        '{{{firstName}}}',
        '{{{lastName}}}',
        '{{{namePrefix}}}',
        '{{{nameTitle}}}',
        '{{{businessTitle}}}',
        '{{{addressGreetings}}}',
        '{{{letterGreetings}}}',
        '{{{cityStateZip}}}',
        '{{{facilities}}}',
        '{{{staff}}}',
        '{{{email}}}',
        '{{{phone}}}'
    ];

    const handleCopyPlaceholder = async (placeholder: string) => {
        try {
            await navigator.clipboard.writeText(placeholder);
            setCopiedPlaceholder(placeholder);
            setTimeout(() => setCopiedPlaceholder(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const transactionTypesQ = useQuery(
        ['transactionTypes'],
        async () => {
            const response = await MailingConfigControllerService.getAll();
            return response || [];
        },
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );

    const mailTemplatesQ = useQuery(
        ['transactionalMailTemplates'],
        () => TransactionalMailTemplateService.getAll(),
        {
            staleTime: 0,
            refetchOnWindowFocus: true,
        }
    );

    const transactionTypes = transactionTypesQ.data || [];
    const mailTemplates: TransactionalMailTemplate[] = mailTemplatesQ.data || [];

    // Template assigned to the language tab the user currently has selected for this event.
    const getMailTemplateForLanguage = (field: keyof ConfirmationSettings, language: string) => {
        const value = confirmationSettings?.[field] as LocalizedMailTemplate;
        const templateId = value?.[language];
        return templateId ? mailTemplates.find(tmpl => tmpl.id === templateId) : undefined;
    };

    const handleEditMailTemplate = (template: TransactionalMailTemplate) => {
        setEditDialogTemplate(template);
    };

    const handleCloneMailTemplate = async (field: keyof ConfirmationSettings, template: TransactionalMailTemplate) => {
        setCloningField(String(field));
        try {
            const {id, insertDate, updateDate, organizationId, chainId, groupId, idV2, cidV2, archived, tags, ...rest} = template;
            const cloned = await TransactionalMailTemplateService.create({
                ...rest,
                name: t('Copy of {{name}}', {name: template.name}),
            });
            await queryClient.invalidateQueries(['transactionalMailTemplates']);
            setCloneDialogTemplate(cloned);
        } finally {
            setCloningField(null);
        }
    };

    const handleOpenMailTemplateMenu = (
        event: React.MouseEvent<HTMLButtonElement>,
        field: keyof ConfirmationSettings
    ) => {
        setMailTemplateMenuAnchorEl(event.currentTarget);
        setMailTemplateMenuField(field);
    };

    const handleCloseMailTemplateMenu = () => {
        setMailTemplateMenuAnchorEl(null);
        setMailTemplateMenuField(null);
    };

    const handleNavigateToMaileon = (maileonMailingId: number | undefined) => {
        window.open(
            `${newsletterAccount?.newsletterAccountLoginUrl}/eagle_kp_webapp/mln/campaigns/triggers?eId=${maileonMailingId}`,
            '_blank'
        );
    };

    const renderLocalizedMailTemplate = (
        field: keyof ConfirmationSettings,
        label: string,
        subtitle?: string,
        dotColor?: string,
        showDivider = true,
    ) => {
        const value = confirmationSettings?.[field] as LocalizedMailTemplate;
        const menuOpen = Boolean(mailTemplateMenuAnchorEl) && mailTemplateMenuField === field;
        const disabledActionTooltip = t('Select a template first');

        const renderActions = (selectedLanguage: string) => {
            const templateToClone = getMailTemplateForLanguage(field, selectedLanguage);
            return (
            <Stack direction="row" alignItems="center" gap={0.5} sx={{height: 40, flexShrink: 0}}>
                <Tooltip title={templateToClone ? t('Edit template') : disabledActionTooltip}>
                    <span>
                        <Button
                            size={'small'}
                            startIcon={<EditOutlinedIcon fontSize="small"/>}
                            disabled={!templateToClone}
                            onClick={() => templateToClone && handleEditMailTemplate(templateToClone)}
                            sx={{height: 32}}
                        >
                            {t('Edit')}
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={templateToClone ? t('More actions') : disabledActionTooltip}>
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            disabled={!templateToClone}
                            onClick={(event) => handleOpenMailTemplateMenu(event, field)}
                        >
                            <MoreVertIcon fontSize="small"/>
                        </IconButton>
                    </span>
                </Tooltip>
                <Menu
                    anchorEl={mailTemplateMenuAnchorEl}
                    open={menuOpen}
                    onClose={handleCloseMailTemplateMenu}
                >
                    <MenuItem
                        disabled={!templateToClone || cloningField === String(field)}
                        onClick={() => {
                            handleCloseMailTemplateMenu();
                            if (templateToClone) void handleCloneMailTemplate(field, templateToClone);
                        }}
                    >
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            {cloningField === String(field)
                                ? <CircularProgress size={16}/>
                                : <ContentCopyIcon fontSize="small"/>}
                            {t('Duplicate')}
                        </Stack>
                    </MenuItem>
                </Menu>
            </Stack>
            );
        };

        const titleNode = dotColor ? (
            <Stack direction="row" alignItems="center" gap={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }}/>
                <span>{label}</span>
            </Stack>
        ) : label;

        return (
            <Stack spacing={2.5}>
                <Property title={titleNode} subtitle={subtitle} sx={{ subtitle: { fontSize: '0.775rem' } }}>
                    <LocalizedMailTemplateField
                        value={value}
                        onChange={(val) => onSettingChange(field, val)}
                        mailTemplates={mailTemplates}
                        actions={renderActions}
                    />
                </Property>
                {showDivider && <Divider/>}
            </Stack>
        );
    };

    const renderLocalizedTransactionType = (
        event: keyof ConfirmationSettings,
        title: string,
        subtitle?: string
    ) => {
        return (
            <Stack spacing={1}>
                <Property title={title} subtitle={subtitle} sx={{ subtitle: { fontSize: '0.775rem' } }}>
                    <LocalizedTransactionTypeField
                        value={confirmationSettings?.[event] as LocalizedConfirmationTemplate}
                        onChange={(value) => onSettingChange(event, value)}
                        transactionTypes={transactionTypes}
                    />
                </Property>
                <Divider/>
            </Stack>
        );
    };

    return (
        <Stack sx={sx} gap={section === 'all' ? (gap ?? 6) : (gap ?? 1)}>
            {showSectionTitle !== false && section !== 'all' && (
                <Typography variant={'subtitle1'} textAlign={'start'} mt={2} fontWeight={'bold'}>
                    {section === 'other' ? t('Other') : t('SendGrid Email Templates')}
                </Typography>
            )}

            {showSendGrid && (
                <Stack gap={section === 'all' ? 2 : 1}>
                    {showSectionTitle !== false && section === 'all' && (
                        <Typography variant={'h5'} textAlign={'start'}>
                            {t('SendGrid Email Templates')}
                        </Typography>
                    )}

                    <Alert severity={'info'}>
                        {t('Select which email template is sent for each reservation event')}
                    </Alert>

                    <Stack
                        gap={2.5}
                    >
                            <>
                                {
                                    mailTemplates.length === 0 &&
                                    <Alert severity="warning">
                                        {t('No templates created yet. Go to the Templates tab to create your first email template.')}
                                    </Alert>
                                }
                                {renderLocalizedMailTemplate(
                                    'reservationPendingMailTemplateIdLocalized',
                                    t('Reservation Pending'),
                                    t('Sent when a reservation is awaiting confirmation.'),
                                    '#ed6c02' // warning / yellow
                                )}
                                {renderLocalizedMailTemplate(
                                    'reservationConfirmedMailTemplateIdLocalized',
                                    t('Reservation Confirmed'),
                                    t('Sent when a reservation has been confirmed.'),
                                    '#2e7d32' // success / green
                                )}
                                {renderLocalizedMailTemplate(
                                    'reservationUpdatedMailTemplateIdLocalized',
                                    t('Reservation Updated'),
                                    t('Sent when reservation details change and "Save & Send Email" is selected.'),
                                    '#0288d1' // info / blue
                                )}
                                {renderLocalizedMailTemplate(
                                    'reservationCompletedMailTemplateIdLocalized',
                                    t('Reservation Completed'),
                                    t('Sent after a reservation has been completed.'),
                                    '#455a64' // blueGray 600 / black
                                )}
                                {renderLocalizedMailTemplate(
                                    'reservationCancelledMailTemplateIdLocalized',
                                    t('Reservation Cancelled'),
                                    t('Sent when a reservation is cancelled.'),
                                    '#d32f2f' // error / red
                                )}
                                {renderLocalizedMailTemplate(
                                    'reservationNoShowMailTemplateIdLocalized',
                                    t('Reservation No-Show'),
                                    t('Sent when a guest does not arrive for their reservation.'),
                                    '#795548', // brown
                                    false
                                )}
                            </>
                    </Stack>
                </Stack>
            )}

            {showOther && (
                <Stack gap={section === 'all' ? 2 : 1.5} sx={{mt: showSendGrid && section !== 'all' ? 2 : undefined}}>
                    {showSectionTitle !== false && (section === 'all' || showSendGrid) && (
                        <Typography
                            variant={section === 'all' ? 'h5' : 'subtitle1'}
                            textAlign={'start'}
                            sx={{fontWeight: section === 'all' ? undefined : 'bold'}}
                        >
                            {t('Other')}
                        </Typography>
                    )}

                    <Stack gap={2}>
                        {/* Maileon template selectors (legacy) */}
                        <AdditionalProperties
                            title={t('Maileon Templates (Legacy)')}
                            subtitle={t('Used as fallback when no SendGrid template is selected above')}
                            variant={'outlined'}
                        >
                            <Stack gap={1}>
                                {transactionTypesQ.isLoading ? (
                                    <Typography>{t('Loading transaction types...')}</Typography>
                                ) : (
                                    <>
                                        {renderLocalizedTransactionType(
                                            "reservationPendingLocalized",
                                            t('Reservation Pending'),
                                            t('Sent when a reservation is awaiting confirmation.')
                                        )}
                                        {renderLocalizedTransactionType(
                                            "reservationConfirmedLocalized",
                                            t('Reservation Confirmed'),
                                            t('Sent when a reservation has been confirmed.')
                                        )}
                                        {renderLocalizedTransactionType(
                                            "reservationCancelledLocalized",
                                            t('Reservation Cancelled'),
                                            t('Sent when a reservation is cancelled.')
                                        )}
                                        {renderLocalizedTransactionType(
                                            "reservationNoShowLocalized",
                                            t('Reservation No-Show'),
                                            t('Sent when a guest does not arrive for their reservation.')
                                        )}
                                        {renderLocalizedTransactionType(
                                            "reservationCompletedLocalized",
                                            t('Reservation Completed'),
                                            t('Sent after a reservation has been completed.')
                                        )}
                                        {renderLocalizedTransactionType(
                                            "reservationUpdatedLocalized",
                                            t('Reservation Updated'),
                                            t('Sent when reservation details change and "Save & Send Email" is selected.')
                                        )}

                                        <Alert severity={'info'} sx={{mt: 2}}>
                                            <Stack gap={2}>
                                                <Typography>
                                                    {t('Select which email template is used for different use cases')}
                                                </Typography>
                                                <Button
                                                    disabled={readOnlyUser}
                                                    onClick={() => setAddNewTransactionTypeDialogOpen(true)}
                                                    variant={'outlined'}
                                                >
                                                    {t('Create new mailing template')}
                                                </Button>
                                                <Button
                                                    disabled={readOnlyUser}
                                                    onClick={() =>
                                                        window.open(`${newsletterAccount?.newsletterAccountLoginUrl}/eagle_kp_webapp/mln/campaigns/triggers`, '_blank')
                                                    }
                                                    variant={'outlined'}
                                                >
                                                    {t('Go to notification mails')}
                                                </Button>
                                            </Stack>
                                        </Alert>
                                    </>
                                )}
                            </Stack>
                        </AdditionalProperties>

                        {hasGuestAppModule && (
                            <AdditionalProperties title={t('Push / App chat')} variant={'outlined'}>
                                <Stack gap={1} mb={2}>
                                    <Typography variant={'caption'} color={'text.secondary'}>
                                        {t('Available placeholders (click to copy)')}:
                                    </Typography>
                                    <Stack direction={'row'} flexWrap={'wrap'} gap={0.5}>
                                        {placeholders.map((placeholder) => (
                                            <Chip
                                                key={placeholder}
                                                label={placeholder}
                                                size={'small'}
                                                onClick={() => handleCopyPlaceholder(placeholder)}
                                                color={copiedPlaceholder === placeholder ? 'success' : 'default'}
                                                variant={copiedPlaceholder === placeholder ? 'filled' : 'outlined'}
                                                sx={{
                                                    cursor: 'pointer',
                                                    fontSize: '0.7rem',
                                                    height: '24px',
                                                    '&:hover': {
                                                        backgroundColor: copiedPlaceholder === placeholder ? undefined : 'action.hover'
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>

                                <Stack gap={1}>
                                    <Property
                                        title={t('Reservation confirmed - Title')}
                                        subtitle={t('Push notification title when reservation is confirmed')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationConfirmedTitleLocalized}
                                            onChange={(value) => onSettingChange('pushReservationConfirmedTitleLocalized', value)}
                                            fullWidth
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation confirmed - Body')}
                                        subtitle={t('Push notification body when reservation is confirmed')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationConfirmedBodyLocalized}
                                            onChange={(value) => onSettingChange('pushReservationConfirmedBodyLocalized', value)}
                                            fullWidth
                                            multiline
                                            rows={3}
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation cancelled - Title')}
                                        subtitle={t('Push notification title when reservation is cancelled')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationCancelledTitleLocalized}
                                            onChange={(value) => onSettingChange('pushReservationCancelledTitleLocalized', value)}
                                            fullWidth
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation cancelled - Body')}
                                        subtitle={t('Push notification body when reservation is cancelled')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationCancelledBodyLocalized}
                                            onChange={(value) => onSettingChange('pushReservationCancelledBodyLocalized', value)}
                                            fullWidth
                                            multiline
                                            rows={3}
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation updated - Title')}
                                        subtitle={t('Push notification title when reservation is updated')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationUpdatedTitleLocalized}
                                            onChange={(value) => onSettingChange('pushReservationUpdatedTitleLocalized', value)}
                                            fullWidth
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation updated - Body')}
                                        subtitle={t('Push notification body when reservation is updated')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationUpdatedBodyLocalized}
                                            onChange={(value) => onSettingChange('pushReservationUpdatedBodyLocalized', value)}
                                            fullWidth
                                            multiline
                                            rows={3}
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation completed - Title')}
                                        subtitle={t('Push notification title when reservation is completed')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationCompletedTitleLocalized}
                                            onChange={(value) => onSettingChange('pushReservationCompletedTitleLocalized', value)}
                                            fullWidth
                                        />
                                    </Property>
                                    <Divider/>

                                    <Property
                                        title={t('Reservation completed - Body')}
                                        subtitle={t('Push notification body when reservation is completed')}
                                    >
                                        <LocalizedTextField
                                            value={confirmationSettings?.pushReservationCompletedBodyLocalized}
                                            onChange={(value) => onSettingChange('pushReservationCompletedBodyLocalized', value)}
                                            fullWidth
                                            multiline
                                            rows={3}
                                        />
                                    </Property>
                                    <Divider/>
                                </Stack>
                            </AdditionalProperties>
                        )}
                    </Stack>
                </Stack>
            )}

            {showOther && (
                <QuickAddTransactionTypeDialog
                    open={addNewTransactionTypeDialogOpen}
                    onClose={() => setAddNewTransactionTypeDialogOpen(false)}
                    onAdded={(transactionType) => {
                        setAddNewTransactionTypeDialogOpen(false);
                        transactionTypesQ.refetch();
                        transactionType.maileonMailingId && handleNavigateToMaileon(transactionType.maileonMailingId);
                    }}
                />
            )}

            {showSendGrid && (
                <CreateTransactionalMailTemplateDialog
                    open={!!cloneDialogTemplate || !!editDialogTemplate}
                    onClose={() => {
                        setCloneDialogTemplate(undefined);
                        setEditDialogTemplate(undefined);
                    }}
                    onSaved={() => {
                        setCloneDialogTemplate(undefined);
                        setEditDialogTemplate(undefined);
                        queryClient.invalidateQueries(['transactionalMailTemplates']);
                    }}
                    templateToEdit={editDialogTemplate ?? cloneDialogTemplate}
                />
            )}
        </Stack>
    );
};

export default ConfirmationSettingsSection;
